import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { envValue } from '@/lib/env'
import type { Session, User } from '@supabase/supabase-js'

export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  // mfaRequired: the user has a verified TOTP factor but this session is still
  // aal1 — the auth gate must route to /mfa before any app content renders.
  | { status: 'authenticated'; session: Session; user: User; mfaRequired: boolean }

function redirectBase(): string {
  return envValue(process.env.EXPO_PUBLIC_APP_URL)
    ?? (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8081')
}

/**
 * Whether this session still owes an MFA step. Computed locally from the JWT's
 * `aal` claim and the factors embedded in the session user — deliberately NOT
 * via supabase.auth.mfa.* here, because calling auth methods from inside
 * onAuthStateChange deadlocks on the client's internal lock.
 */
export function mfaRequiredFor(session: Session): boolean {
  const hasVerifiedFactor = session.user.factors?.some(f => f.status === 'verified') ?? false
  if (!hasVerifiedFactor) return false
  try {
    const payload = JSON.parse(
      atob(session.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { aal?: string }
    return payload.aal !== 'aal2'
  } catch {
    // Unreadable token — fail closed and ask for the second factor.
    return true
  }
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({ status: 'loading' })

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION on mount with the session
    // restored from storage — more reliable than getSession() which can
    // return null before the async storage read completes.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setAuthState({
          status: 'authenticated',
          session,
          user: session.user,
          mfaRequired: mfaRequiredFor(session),
        })
      } else {
        setAuthState({ status: 'unauthenticated' })
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signInWithOtp = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${redirectBase()}/auth/callback`,
      },
    })
    if (error) throw error
  }

  const verifyOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })
    if (error) throw error
  }

  /** Day-to-day sign-in. Returns whether the session still needs a TOTP code. */
  const signInWithPassword = async (email: string, password: string): Promise<{ mfaRequired: boolean }> => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return { mfaRequired: data.session ? mfaRequiredFor(data.session) : false }
  }

  /**
   * Create a business account with a password. Signing up always creates a NEW
   * tenant, owned by this person — the business name rides in the auth user's
   * metadata and is what `v1-tenant-config` provisions the tenant from on first
   * sign-in. With email confirmation on (production) no session is returned; the
   * user must click the verification link first, which lands on /auth/callback.
   */
  const signUpWithPassword = async (
    email: string,
    password: string,
    /** Only for a business sign-up — the marker v1-tenant-config provisions a tenant on. */
    businessName?: string,
  ): Promise<{ needsVerification: boolean }> => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${redirectBase()}/auth/callback`,
        ...(businessName ? { data: { business_name: businessName } } : {}),
      },
    })
    if (error) throw error
    return { needsVerification: !data.session }
  }

  /** Emails a recovery link; the callback routes type=recovery to /reset-password. */
  const requestPasswordReset = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${redirectBase()}/auth/callback`,
    })
    if (error) throw error
  }

  /** Sets a new password on the signed-in user (recovery flow and Settings). */
  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw error
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  return {
    authState,
    signInWithOtp,
    verifyOtp,
    signInWithPassword,
    signUpWithPassword,
    requestPasswordReset,
    updatePassword,
    signOut,
  }
}
